import Header from "../../components/header/Header";
import styles from "./styles/profile.module.scss";
import SideBar from "../../components/sideBar/SideBar";
import AvatarBg from "../../components/icons/avatarbg";
import Footer from "../../components/footer/Footer";
import AccountSettings from "./components/AccountSettings";
import PrepaymentPage from "./components/Prepayment";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { ROUTES } from "../../constants/routes";
import { useAppSelector } from "../../store/store";
import ModalWrapper from "../../components/shared/ModalWrapper/ModalWrapper";
import CardAddedModal from "./components/CardAddedModal";
import CardRemoveModal from "./components/CardRemoveModal";
import CardRemoveConfirmModal from "./components/CardRemoveConfirmModal";
import RemoveSubscriptionModal from "./components/RemoveSubscriptionModal";
import SuccessRemoveSubscriptionModal from "./components/SuccessRemoveSubscriptionModal";
import useWindowSize from "../../hooks/useWindowSize";
import ChevronRight from "../../components/icons/ChevronRight";
import { setAccountTab } from "../../store/slices/navigation.slice";
import { useDispatch } from "react-redux";
import React, { useEffect, useRef, useState } from "react";
import { useLogin } from "../../hooks/useLogin";
import UploadPhoto from "../../components/icons/profile/uploadPhoto";
import { useUploadPhoto } from "../../utils/hooks/useUploadPhoto";
import MobileLinks from "../../components/footer/MobileLinks";
import ChangeSubModal from "./components/ChangeSubModal";
import { getUserImageUrl } from "../../utils/imageUrl";
import PremiumStar from "../../components/icons/profile/PremiumStar";
import GradientBackdrop from "../../components/shared/GradientBackdrop/GradientBackdrop";
import LinkGuide from "../../components/shared/LinkGuide/LinkGuide";
import { getLatestSubscriptionEndDate, formatSubscriptionDate } from "../../utils/subscription";

const Profile = () => {
  const currentYear = new Date().getFullYear();
  const { uploadPhoto } = useUploadPhoto();
  const [uploadResult, setUploadResult] = useState<any>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { modal } = useAppSelector((state: any) => state.modals);
  const userData = useAppSelector((state: any) => state.auth.userData);
  const userAvatar = userData?.picture_url;
  const userAvatarUrl = getUserImageUrl(uploadResult || userAvatar);
  const userId = userData?.hashed_id;

  const { currentTab } = useAppSelector((state: any) => state.navigation);
  const { windowSize } = useWindowSize();
  const { logout } = useLogin();

  const firstName = userData?.username;
  const hasPremium = Boolean(userData?.has_monthly_subscription || userData?.has_yearly_subscription);
  const premiumEndDate = formatSubscriptionDate(getLatestSubscriptionEndDate(userData));

  const currentTabHandler = (value: number) => {
    dispatch(setAccountTab(value));
  };

  const settingsHandler = () => {
    if (windowSize === "mobile") {
      navigate(ROUTES.MOBILE_ACCOUNT_SETTINGS);
      return;
    }

    currentTabHandler(2);
  };

  const paymentHandler = () => {
    if (windowSize === "mobile") {
      navigate(ROUTES.MOBILE_PAYMENT_DETAILS);
      return;
    }

    currentTabHandler(1);
  };

  const handleClickFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const previewUrl = URL.createObjectURL(file);
      setUploadResult(previewUrl);
      try {
        await uploadPhoto(file);
      } catch (err) {
        console.error("Ошибка загрузки:", err);
      }
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const dirData = i18n.language === "ar" ? "rtl" : "ltr";

  return (
    <>
      <div className={styles.profilePageWrapper}>
        <Header />
        <GradientBackdrop/>
        <SideBar />
        <div className={styles.profilePageContentWrapper}>
          <div className={styles.pageLeftContent}>
            <div className={styles.avatarBlock}>
              <div
                dir={dirData}
                onClick={handleClickFileInput}
                className={styles.avatarMain}
              >
                <AvatarBg />
                <span>{(firstName || "User")[0]}</span>
                {userAvatarUrl && (
                  <img
                    src={userAvatarUrl}
                    alt=""
                    className={styles.avatarImage}
                    onLoad={(e) => {
                      e.currentTarget.style.display = "block";
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
                <div className={styles.uploadPhotoHover}>
                  <UploadPhoto />
                </div>
              </div>
              <div>
                <div className={styles.avatarBlockName}>{firstName || "User"}</div>
                <div className={styles.avatarBlockId}>ID: {userId}</div>
              </div>
            </div>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            {windowSize !== "mobile" && (
              <div className={styles.navigationBlock}>
                <div className={`${currentTab === 1 ? styles.active : ""}`} onClick={paymentHandler}>
                  {t("subAndPay")}
                </div>
                <div className={`${currentTab === 2 ? styles.active : ""}`} onClick={settingsHandler}>
                  {t("settings")}
                </div>
              </div>
            )}
            {windowSize === "mobile" && (
              <div className={styles.navigationBlock}>
                <div className={styles.mobileNavItem} onClick={paymentHandler}>
                  <span>{t("subAndPay")}</span>
                  <ChevronRight />
                </div>
                <div className={styles.mobileNavItem} onClick={settingsHandler}>
                  <span>{t("settings")}</span>
                  <ChevronRight />
                </div>
              </div>
            )}
            <div className={styles.prePayWrapper}>
              {hasPremium ? (
                <div className={styles.premiumStatusRow}>
                  <PremiumStar className={styles.premiumStatusIcon} />
                  <div className={styles.premiumStatusText}>
                    <span className={styles.premiumName}>“{t("premiumName")}”</span>
                    {premiumEndDate ? ` ${t("premiumValidUntil")} ${premiumEndDate}` : ` ${t("premiumActive")}`}
                  </div>
                </div>
              ) : (
                <Link
                  to={ROUTES.DISCOUNT}
                  className={`${styles.premiumStatusRow} ${styles.premiumStatusButton}`}
                >
                  <PremiumStar className={styles.premiumStatusIcon} />
                  <span className={styles.premiumSubscribeText}>
                    <span>{t("premiumAccessPrompt")}</span>
                    <span className={styles.premiumSubscribeLink}>{t("premiumSubscribeCta")}</span>
                  </span>
                </Link>
              )}
            </div>
            <MobileLinks />
            <div
              onClick={() => {
                logout();
                navigate(ROUTES.FOR_YOU);
              }}
              className={styles.logOut}
            >
              {t("logOut")}
            </div>
            {windowSize === "mobile" && (
              <a className={styles.app} href="/">
                © {currentYear} HIVIEWER.  {t("copyright")}.
              </a>
            )}
          </div>
          {windowSize !== "mobile" && (
            <div className={styles.pageRightContent}>
              {currentTab === 2 && <AccountSettings />}
              {currentTab === 1 && <PrepaymentPage />}
            </div>
          )}
        </div>
      </div>
      <Footer source={""} />
      {modal && (
        <ModalWrapper>
          {modal === "cardAdded" && <CardAddedModal />}
          {modal === "cardRemove" && <CardRemoveModal />}
          {modal === "cardRemoveConfirm" && <CardRemoveConfirmModal />}
          {modal === "removeSubscription" && <RemoveSubscriptionModal />}
          {modal === "removeSubscriptionSuccess" && <SuccessRemoveSubscriptionModal />}
          {modal === "changeSub" && <ChangeSubModal />}
        </ModalWrapper>
      )}
      <LinkGuide/>
    </>
  );
};

export default Profile;
